/** News channel mascot animation flow — API helpers and constants. */

export const DEFAULT_MASCOT_STYLE_TEMPLATE_ID = 'f4bb1008-af22-4e1c-91b2-8cee15a91e81';

import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import { getBackendBaseUrl } from '@/services/config/env';

export function styleTemplateSampleUrl(template, backendUrl) {
  if (!template) return null;
  const raw = template.sample_image_url || template.preview_url;
  if (!raw) return null;
  const base = (backendUrl || getBackendBaseUrl() || 'http://127.0.0.1:8000').replace(/\/+$/, '');
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return mediaUrlForPlayback(raw);
  }
  if (raw.startsWith('/')) return `${base}${raw}`;
  return `${base}/${raw}`;
}

export const MASCOT_IMAGE_PROVIDERS = [
  { value: 'nano_banana', label: 'Gemini Nano Banana' },
  { value: 'fotor', label: 'Fotor cartoon + Nano Banana poses' },
  { value: 'inhouse', label: 'Inhouse' },
];

export const MASCOT_FLOW_STEPS = [
  { id: 'setup', label: 'Setup', path: 'setup' },
  { id: 'upload', label: 'Photos', path: 'upload' },
  { id: 'styles', label: 'Style', path: 'styles' },
  { id: 'sheet', label: 'Sheet', path: 'sheet' },
  { id: 'animate', label: 'Animate', path: 'animate' },
  { id: 'export', label: 'Export', path: 'export' },
];

export const MASCOT_BACKGROUND_MODES = [
  { value: 'transparent', label: 'Transparent', hint: 'Alpha WebM for overlays', styleHint: 'PNG with transparent background' },
  { value: 'white', label: 'White', hint: 'Solid white backdrop (MP4)', styleHint: 'Solid white behind the mascot' },
  { value: 'green', label: 'Green screen', hint: 'Chroma green #00FF00 (MP4)', styleHint: 'Chroma green #00FF00 — best for character sheet' },
  { value: 'custom_image', label: 'Custom image', hint: 'Upload a background plate', styleHint: 'Composite mascot onto your uploaded image' },
  { value: 'describe', label: 'Describe background', hint: 'AI-generated scene from text', styleHint: 'AI-generated scene behind the mascot' },
];

export const DEFAULT_MASCOT_STYLE_BACKGROUND_MODE = 'green';

export function mascotBackgroundPayload(mode, description, customImageUrl) {
  return {
    mode: mode || 'transparent',
    description: String(description || '').trim(),
    custom_image_url: customImageUrl || null,
  };
}

export function isExportVideoEntry(entry) {
  if (!entry) return false;
  const kind = String(entry.kind || '').toLowerCase();
  if (kind === 'local_webm' || kind === 'local_mp4' || kind === 'seedance2') return true;
  const url = String(entry.video_url || entry.media_url || entry.source_url || '');
  return /\.(webm|mp4)(\?|$)/i.test(url);
}

export function isHistoryVideoEntry(entry) {
  return isExportVideoEntry(entry);
}

export function historyMediaUrl(entry, backendUrl) {
  if (!entry) return null;
  const raw = entry.video_url || entry.source_url || entry.media_url || entry.preview_url;
  return mascotMediaUrl(raw, backendUrl);
}

function sortHistoryDesc(rows) {
  return [...rows].sort((a, b) => String(b.completed_at || b.created_at || '')
    .localeCompare(String(a.created_at || a.completed_at || '')));
}

function historyEntryKey(entry) {
  if (!entry) return '';
  return String(entry.id || entry.video_url || entry.source_url || entry.media_url || '');
}

function collectExportHistoryRows(project) {
  if (!project) return [];
  const rows = [];
  const seen = new Set();
  const add = (entry) => {
    if (!isExportVideoEntry(entry)) return;
    const key = historyEntryKey(entry);
    if (key && seen.has(key)) return;
    if (key) seen.add(key);
    rows.push(entry);
  };

  (project.export_history || []).forEach(add);
  (project.video_history || []).forEach(add);

  const legacy = project.animation?.video_url;
  if (legacy && !seen.has(legacy)) {
    add({
      id: `legacy-animation-${project.animation.rendered_at || 'latest'}`,
      kind: 'local_webm',
      preset_id: project.animation.preset_id,
      preset_label: project.animation.preset_label,
      video_url: legacy,
      status: 'completed',
      duration_sec: project.animation.duration_sec,
      created_at: project.animation.rendered_at,
      completed_at: project.animation.rendered_at,
    });
  }

  return sortHistoryDesc(rows);
}

export function mergedExportHistory(project) {
  const rows = collectExportHistoryRows(project);
  if (rows.length) return rows;
  return mergedVideoHistory(project);
}

export function mergedOutputHistory(project) {
  const server = project?.output_history;
  if (Array.isArray(server) && server.length) {
    return sortHistoryDesc(server);
  }
  return mergedExportHistory(project);
}

export function mergedVideoHistory(project) {
  return collectExportHistoryRows(project);
}

export function projectReadyForSheet(project) {
  const chars = project?.characters || [];
  return chars.length > 0 && chars.every((c) => c.reference_url);
}

export function projectReadyForStyles(project) {
  return projectReadyForSheet(project);
}

export function projectStylesApplied(project) {
  if (!projectReadyForStyles(project)) return false;
  if (project?.style_step_skipped) return true;
  const tid = String(project?.default_style_template_id || '').trim();
  if (!tid) return true;
  return (project.characters || []).every(
    (c) => c.style_template_id === tid && c.styled_reference_url,
  );
}

/** True when the user may leave the Style step (applied, failed, or styling skipped). */
export function canProceedFromStyles(project) {
  if (!projectReadyForStyles(project)) return false;
  if (project?.style_step_skipped) return true;
  const chars = (project.characters || []).filter((c) => c.reference_url);
  if (!chars.length) return false;
  const tid = String(project?.default_style_template_id || '').trim();
  if (!tid) return true;
  return chars.every(
    (c) => Boolean(c.styled_reference_url) || c.style_status === 'failed',
  );
}

export function mascotMediaUrl(url, backendUrl) {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) {
    return mediaUrlForPlayback(url);
  }
  const base = (backendUrl || getBackendBaseUrl() || 'http://127.0.0.1:8000').replace(/\/+$/, '');
  return url.startsWith('/') ? `${base}${url}` : `${base}/${url}`;
}

export function projectSheetReady(project) {
  if (!project) return false;
  if (project.status === 'sheet_ready') return true;
  return (project.character_sheets || []).length > 0;
}

export function mascotStepFromProject(project) {
  if (!project) return 'setup';
  const hist = mergedExportHistory(project);
  if (hist.some((h) => h.status === 'completed' && isExportVideoEntry(h))) return 'export';
  if (project.status === 'sheet_ready' || (project.character_sheets || []).length) return 'animate';
  if (project.status === 'generating_sheet') return 'sheet';
  if ((project.character_sheets || []).length) return 'sheet';
  if (projectReadyForStyles(project)) {
    return projectStylesApplied(project) ? 'sheet' : 'styles';
  }
  return 'upload';
}

export function mascotAdjacentStep(stepId, direction = 'next') {
  const steps = MASCOT_FLOW_STEPS.map((s) => s.id);
  const idx = steps.indexOf(stepId);
  if (idx < 0) return null;
  const delta = direction === 'back' ? -1 : 1;
  return steps[idx + delta] ?? null;
}

export function mascotStepProgressIndex(project) {
  return MASCOT_FLOW_STEPS.findIndex((s) => s.id === mascotStepFromProject(project));
}

export function sheetRowsFromProject(project) {
  const sheets = project?.character_sheets || [];
  return sheets.map((row, idx) => ({
    characterId: row.character_id || project?.characters?.[idx]?.id,
    name: row.name || project?.characters?.[idx]?.name || `Character ${idx + 1}`,
    sheet: row.sheet || {},
    cutouts: row.cutouts || {},
  }));
}

export function appearanceProfileBadges(profile) {
  if (!profile || typeof profile !== 'object') return [];
  const badges = [];
  if (profile.gender && profile.gender !== 'unknown') {
    badges.push(String(profile.gender).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));
  }
  if (profile.age_group && profile.age_group !== 'unknown') {
    badges.push(String(profile.age_group).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));
  }
  if (typeof profile.estimated_age_years === 'number') {
    badges.push(`~${profile.estimated_age_years} yrs`);
  }
  ['ethnicity_or_heritage', 'skin_tone', 'body_type'].forEach((key) => {
    const val = String(profile[key] || '').trim();
    if (val) badges.push(val);
  });
  const hair = profile.hair || {};
  const hairBits = [hair.color, hair.style].map((x) => String(x || '').trim()).filter(Boolean);
  if (hairBits.length) badges.push(`${hairBits.join(' ')} hair`);
  (profile.distinctive_features || []).slice(0, 2).forEach((feat) => {
    const text = String(feat || '').trim();
    if (text) badges.push(text);
  });
  return badges.slice(0, 8);
}

export function appearanceProfileSummary(profile) {
  if (!profile?.consistency_directive) return '';
  return String(profile.consistency_directive).trim();
}

export const MASCOT_GENDER_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'ambiguous', label: 'Ambiguous' },
  { value: 'unknown', label: 'Unknown' },
];

export const MASCOT_AGE_GROUP_OPTIONS = [
  { value: 'child', label: 'Child' },
  { value: 'teen', label: 'Teen' },
  { value: 'young_adult', label: 'Young adult' },
  { value: 'adult', label: 'Adult' },
  { value: 'middle_aged', label: 'Middle aged' },
  { value: 'senior', label: 'Senior' },
  { value: 'unknown', label: 'Unknown' },
];

export const MASCOT_HEIGHT_OPTIONS = [
  { value: 'short', label: 'Short' },
  { value: 'average', label: 'Average' },
  { value: 'tall', label: 'Tall' },
  { value: 'unknown', label: 'Unknown' },
];

export function listToMultilineText(items) {
  return (items || []).map((item) => String(item || '').trim()).filter(Boolean).join('\n');
}

export function multilineTextToList(text, maxItems = 8) {
  return String(text || '')
    .split(/[\n,]+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, maxItems);
}

export function appearanceProfileFormState(profile) {
  const p = profile || {};
  const hair = p.hair || {};
  return {
    gender: p.gender || 'unknown',
    age_group: p.age_group || 'unknown',
    estimated_age_years: typeof p.estimated_age_years === 'number' ? String(p.estimated_age_years) : '',
    ethnicity_or_heritage: p.ethnicity_or_heritage || '',
    skin_tone: p.skin_tone || '',
    hair_color: hair.color || '',
    hair_style: hair.style || '',
    hair_length: hair.length || '',
    facial_features: p.facial_features || '',
    body_type: p.body_type || '',
    height_impression: p.height_impression || 'unknown',
    distinctive_features: listToMultilineText(p.distinctive_features),
    clothing_style: p.clothing_style || '',
    accessories: listToMultilineText(p.accessories),
    expression_default: p.expression_default || '',
    consistency_directive: p.consistency_directive || '',
  };
}

export function appearanceProfilePayloadFromForm(form, mode = 'full') {
  if (mode === 'consistency_only') {
    return {
      mode: 'consistency_only',
      consistency_directive: String(form.consistency_directive || '').trim(),
    };
  }
  const ageRaw = String(form.estimated_age_years || '').trim();
  const ageYears = ageRaw && /^\d+$/.test(ageRaw) ? Number(ageRaw) : null;
  return {
    mode: 'full',
    gender: form.gender || 'unknown',
    age_group: form.age_group || 'unknown',
    estimated_age_years: ageYears,
    ethnicity_or_heritage: String(form.ethnicity_or_heritage || '').trim(),
    skin_tone: String(form.skin_tone || '').trim(),
    hair: {
      color: String(form.hair_color || '').trim(),
      style: String(form.hair_style || '').trim(),
      length: String(form.hair_length || '').trim(),
    },
    facial_features: String(form.facial_features || '').trim(),
    body_type: String(form.body_type || '').trim(),
    height_impression: form.height_impression || 'unknown',
    distinctive_features: multilineTextToList(form.distinctive_features),
    clothing_style: String(form.clothing_style || '').trim(),
    accessories: multilineTextToList(form.accessories),
    expression_default: String(form.expression_default || '').trim(),
    consistency_directive: String(form.consistency_directive || '').trim(),
  };
}

export function dualCharacterPresets(presets) {
  return (presets || []).filter((p) => (p.min_characters || 1) >= 2);
}

export function singleCharacterPresets(presets) {
  return (presets || []).filter((p) => (p.min_characters || 1) < 2);
}

export const MASCOT_ANIMATION_JOB_TYPE = 'mascot_animation_pipeline';

export function mascotProjectIdFromJob(job) {
  const mascotId = String(job?.mascot_project_id || '').trim();
  if (mascotId) return mascotId;
  const projectId = String(job?.project_id || '').trim();
  const storyId = String(job?.story_id || '').trim();
  if (projectId && (!storyId || projectId !== storyId)) return projectId;
  return storyId || null;
}

/** Resume the multipage mascot flow at the last saved project step (history loads on open). */
export function mascotJobResumePath(job) {
  const projectId = mascotProjectIdFromJob(job);
  if (!projectId) return '/mascot/create';
  return `/mascot/${projectId}`;
}
